import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ShoppingBag, Menu, X, ArrowUpRight } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'
import { useBasket } from '../contexts/BasketContext'
import CartDrawer from './CartDrawer'
import AddedToCartModal from './AddedToCartModal'
import CurrencySelector from './CurrencySelector'
import { useServerStatus } from '../hooks/useServerStatus'
import { CONNECT_URL, DiscordIcon } from './public/brand'
import '../styles/public.css'

const NAV = [
  { path: '/', label: 'Home', end: true },
  { path: '/store', label: 'Store' },
  { path: '/rules', label: 'Rules' },
  { path: '/gang-rules', label: 'Gang Rules' },
]

const LEGAL = [
  { path: '/privacy', label: 'Privacy Policy' },
  { path: '/refund', label: 'Refund Policy' },
  { href: 'https://www.tebex.io/terms', label: 'Tebex Terms' },
]

const PublicLayout = ({ children }) => {
  const tenant = useTenant()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, login } = useAuth()
  const { enabled: cartEnabled, itemCount, openCart } = useBasket()
  const status = useServerStatus()

  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close the mobile menu + jump to top on every route change (keep #anchors).
  useEffect(() => {
    setMenuOpen(false)
    if (!location.hash) window.scrollTo(0, 0)
  }, [location.pathname, location.hash])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const handleStaff = () => (isAuthenticated ? navigate('/staff') : login())
  const solid = scrolled || menuOpen

  return (
    <div className="mw-public min-h-screen flex flex-col">
      {/* ===== Header ===== */}
      <header className="fixed inset-x-0 top-0 z-40 px-3 sm:px-5 pt-3">
        <div
          className={'max-w-[1320px] mx-auto h-[64px] pl-5 pr-2.5 lg:pl-7 flex items-center gap-8 rounded-full transition-all duration-500 ease-[cubic-bezier(.2,.7,.2,1)] ' +
            (solid ? 'mw-glass shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]' : 'border border-transparent')}
        >
          <Link to="/" className="flex items-center shrink-0" aria-label={tenant.branding.brandName + ' home'}>
            <img src={tenant.branding.headerLogo} alt={tenant.branding.brandName} className="h-9 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-2" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) => 'px-3.5 py-2 rounded-full text-[14px] font-medium transition-colors ' + (isActive ? 'text-white bg-white/[0.09]' : 'text-[#a6a8ae] hover:text-white')}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <div className="hidden sm:block"><CurrencySelector /></div>
            {cartEnabled && (
              <button
                onClick={openCart}
                aria-label={'Open cart' + (itemCount ? ' (' + itemCount + ' items)' : '')}
                className="relative w-10 h-10 rounded-full flex items-center justify-center text-[#b9bbc1] hover:text-white hover:bg-white/[0.08] transition-colors"
              >
                <ShoppingBag className="w-[19px] h-[19px]" strokeWidth={1.75} />
                {itemCount > 0 && (
                  <span className="absolute top-0.5 right-0 min-w-[17px] h-[17px] px-1 rounded-full bg-[#e5233b] text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                    {itemCount}
                  </span>
                )}
              </button>
            )}
            <a href={CONNECT_URL} className="hidden lg:inline-flex mw-btn mw-btn-primary mw-btn-sm ml-1.5">Play now</a>
            <button onClick={handleStaff} className="hidden md:inline-flex mw-btn mw-btn-ghost mw-btn-sm">
              {isAuthenticated ? 'Dashboard' : 'Staff'}
            </button>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/[0.08]"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* ===== Mobile menu ===== */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-[#07080b] pt-[84px] mw-fade flex flex-col">
          <nav className="flex-1 px-6 pt-8 flex flex-col" aria-label="Mobile">
            {NAV.map((item, i) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) => 'mw-display mw-rise text-[56px] py-1 ' + (isActive ? 'text-white' : 'text-[#5d6068]')}
                style={{ '--mw-delay': i * 60 + 'ms' }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-6 pb-8 space-y-3">
            <div className="flex items-center justify-between pl-5 pr-2 py-1 rounded-full bg-white/[0.04]">
              <span className="mw-mono text-[#8b8d94]">Currency</span>
              <CurrencySelector />
            </div>
            <a href={CONNECT_URL} className="mw-btn mw-btn-primary w-full">Play now</a>
            <button onClick={handleStaff} className="mw-btn mw-btn-ghost w-full">{isAuthenticated ? 'Open dashboard' : 'Staff login'}</button>
          </div>
        </div>
      )}

      <main className="flex-1">{children}</main>

      {/* ===== Footer ===== */}
      <footer className="relative px-3 sm:px-5 pb-3 sm:pb-5 pt-5">
        <div className="max-w-[1400px] mx-auto rounded-[32px] bg-white/[0.025] ring-1 ring-white/[0.05] px-6 sm:px-10 lg:px-14 pt-16 pb-8">
          <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.1fr]">
            <div>
              <img src={tenant.branding.headerLogo} alt={tenant.branding.brandName} className="h-8 w-auto mb-6" />
              <p className="text-[14px] text-[#8b8d94] leading-relaxed max-w-xs">
                A semi-serious FiveM roleplay city. Heists, gangs, and the police who chase them.
              </p>
              <a
                href={tenant.inviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 text-[13px] text-white hover:text-[#c7c9ce] transition-colors"
              >
                <DiscordIcon className="w-4 h-4" /> discord.gg/mwrp <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <FooterCol title="Explore">
              {NAV.map((n) => <FooterLink key={n.path} to={n.path}>{n.label}</FooterLink>)}
            </FooterCol>

            <FooterCol title="Legal">
              {LEGAL.map((n) => <FooterLink key={n.label} to={n.path} href={n.href}>{n.label}</FooterLink>)}
            </FooterCol>

            <FooterCol title="Server">
              <div className="flex items-center gap-2.5 text-[14px] text-white">
                <span className={'mw-live ' + (status && status.online ? 'text-[#34d399]' : status ? 'text-[#e5233b]' : 'text-[#5d6068]')} />
                {status == null ? 'Checking' : status.online ? 'Online' : 'Offline'}
              </div>
              {status && status.online && (
                <p className="text-[14px] text-[#8b8d94] tabular-nums">
                  <span className="text-white">{status.clients}</span> / {status.maxClients} in the city
                </p>
              )}
              <a href={CONNECT_URL} className="mw-btn mw-btn-primary mw-btn-sm mt-2 w-fit">Connect now</a>
            </FooterCol>
          </div>

          <div className="mt-16 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row gap-3 sm:items-center justify-between text-[12px] text-[#5d6068]">
            <span>&copy; {new Date().getFullYear()} MostWanted RP &middot; Powered by StreamClash</span>
            <span>Payments &amp; fulfilment by Tebex &middot; Not affiliated with Rockstar Games</span>
          </div>
        </div>
      </footer>

      <CartDrawer />
      <AddedToCartModal />
    </div>
  )
}

function FooterCol({ title, children }) {
  return (
    <div>
      <p className="mw-mono text-[#5d6068] mb-5">{title}</p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

function FooterLink({ to, href, children }) {
  const cls = 'text-[14px] text-[#a6a8ae] hover:text-white transition-colors w-fit'
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children}</a>
  return <Link to={to} className={cls}>{children}</Link>
}

export default PublicLayout
